import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, Card, Flex, Form, FormInstance, Modal, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { isAxiosError } from 'axios'
import { useAuth } from '../../auth'
import { Chat, ChatMessageProps } from '../../components/chat/chat'
import { useAppConnections, useConfigureTools } from '../hooks'
import {
    AppConnection,
    ConfigurationAnswer,
    ConfigurationQuestion,
    ConfigureToolsInput,
    ToolProposal,
    ToolSelection,
} from '../types'
import { actionKey, maxActions, toolConfiguration } from '../tool-configuration'
import { approveCapability } from '../capability-review'
import { summarizeAnswers } from '../configuration-answer-summary'
import { policyReady } from '../action-policy'
import { CapabilityReview } from './capability-review'
import { AnswerDraft, QuestionForm, answered, answersComplete, serializeAnswers } from './configuration-questions'

type ConfigurationDialogProps = {
    connectionIds: string[]
    invalid: boolean
    // Make a proposal from the campaign instructions as soon as the dialog opens.
    autoStart: boolean
    onClose: () => void
    onApply: (tools: ToolSelection[]) => void
}

type Attempt = {
    message?: string
    sendAnswers?: boolean
    restart?: boolean
    answers?: Record<string, AnswerDraft>
}

type ProposalRequest = {
    input: ConfigureToolsInput
    text?: string
    restart: boolean
    proposalId?: string | null
    draft: ToolSelection[]
    answers: ConfigurationAnswer[]
}

export function ConfigurationDialog({ connectionIds, invalid, autoStart, onClose, onApply }: ConfigurationDialogProps) {
    const { t } = useTranslation()
    const { activeCompany } = useAuth()
    const form = Form.useFormInstance()
    const prompt = useCampaignField<string>(form, ['data', 'prompt'])
    const locale = useCampaignField<string>(form, 'locale')
    const title = useCampaignField<string>(form, 'title')
    const baseline = useCampaignField<ToolSelection[]>(form, 'tools') ?? []
    const connections = useAppConnections()
    const mutation = useConfigureTools()

    const [proposal, setProposal] = useState<ToolProposal>()
    // When adjusting, start from the current configuration so the user sees what Evaa can already do.
    const [draft, setDraft] = useState<ToolSelection[]>(() => (autoStart ? [] : baseline))
    const [messages, setMessages] = useState<ChatMessageProps[]>(() => [
        {
            role: 'assistant',
            content: autoStart
                ? t('I’m reading your campaign instructions and will propose what I can do with these apps.')
                : t(
                      'What would you like to change? You can also let me make a new proposal from your campaign instructions.'
                  ),
        },
    ])
    const [answers, setAnswers] = useState<Record<string, AnswerDraft>>({})
    // Hides the question form after sending until Evaa returns a new proposal.
    const [submitted, setSubmitted] = useState(false)
    const [invalidDraft, setInvalidDraft] = useState<Record<string, boolean>>({})

    const input = {
        prompt: prompt ?? '',
        title: title ?? '',
        locale: locale ?? 'nl_NL',
        tools: toolConfiguration(baseline),
        connectionIds,
    }

    const { snapshot, stale, isStillCurrent, markRequested } = useRequestSnapshot({
        companyId: activeCompany?.id,
        ...input,
    })

    const busy = useRef(false)
    const started = useRef(false)
    const lastAttempt = useRef<Attempt>({})

    const connected = connections.data?.filter((app) => app.status === 'connected') ?? []
    const disconnected = hasDisconnectedApp(connected, connectionIds, draft)
    const locallyInvalid = Object.values(invalidDraft).some(Boolean)
    const hasPrompt = !!input.prompt.trim()
    const questions = proposal?.questions ?? []
    const asking = questions.length > 0 && !submitted

    const isGenerateBlocked = () => busy.current || !hasPrompt || invalid || locallyInvalid || disconnected

    const showProposal = (result: ToolProposal, restart: boolean) => {
        setProposal(result)
        setDraft(result.actions.map((action) => action.tool))
        setAnswers({})
        setSubmitted(false)
        setInvalidDraft({})
        const reply: ChatMessageProps = {
            role: 'assistant',
            content: result.message,
        }

        setMessages((current) => [...(restart ? [] : current), reply])
    }

    const generate = async (attempt: Attempt) => {
        const { message, sendAnswers = false, restart = false, answers: given = answers } = attempt
        const text = message?.trim()

        if (isGenerateBlocked() || (stale && !restart)) {
            return
        }

        if (message !== undefined && !text) {
            return
        }

        lastAttempt.current = {
            message,
            sendAnswers,
            restart,
            answers: given,
        }
        busy.current = true
        markRequested()
        mutation.reset()

        if (text) {
            setMessages((current) => [
                ...current,
                {
                    role: 'user',
                    content: text,
                },
            ])
        }

        try {
            const request = proposalRequest({
                input,
                text,
                restart,
                proposalId: proposal?.proposalId,
                draft,
                answers: answersToSend(questions, given, sendAnswers, text),
            })

            const result = await mutation.mutateAsync(request)

            if (!isStillCurrent(snapshot)) {
                return
            }

            showProposal(result, restart)
        } catch {
            // Keep the draft and answers; retry the same failed request without duplicating history.
            if (text) {
                setMessages((current) => current.slice(0, -1))
            }
        } finally {
            busy.current = false
        }
    }

    const submitAnswers = (value: Record<string, AnswerDraft>) => {
        const filled = questions.filter((question) => answered(question, value[question.id]))
        const summary = filled.length ? summarizeAnswers(filled, value, t('None')) : t('No answers')

        setSubmitted(true)
        setMessages((current) => [
            ...current,
            {
                role: 'user',
                content: summary,
            },
        ])
        void generate({
            sendAnswers: true,
            answers: value,
        })
    }

    const changeDraft = (next: ToolSelection[]) => {
        if (busy.current || stale) {
            return
        }

        setDraft(next)
        setInvalidDraft((current) =>
            Object.fromEntries(next.map((tool) => [actionKey(tool), current[actionKey(tool)] ?? false]))
        )
    }

    const markInvalid = (key: string, invalid: boolean) =>
        setInvalidDraft((current) => ({ ...current, [key]: invalid }))

    const apply = () => {
        onApply(approveCapability(draft, true))
        onClose()
    }

    useEffect(() => {
        if (!autoStart || started.current || connections.isPending) {
            return
        }

        started.current = true
        void generate({})
    })

    const hasOpenQuestions = questions.some((question) => question.required) || Object.keys(answers).length > 0

    // The first reason that applies is shown next to the apply button.
    const applyBlockers: [boolean, string][] = [
        [!proposal, t('Make a proposal first')],
        [mutation.isPending, t('Wait for Evaa to finish')],
        [stale, t('Campaign settings changed. Generate a new proposal before applying.')],
        [hasOpenQuestions, t('Answer Evaa’s questions in the chat to finish the setup.')],
        [locallyInvalid || invalid, t('Correct the invalid value in Advanced settings.')],
        [disconnected, t('Reconnect the selected apps first.')],
        [draft.length > maxActions, t('This proposal has too many actions. Ask Evaa to simplify it.')],
        [
            draft.some((tool) => !policyReady(tool)),
            t('Complete the missing settings below, or describe them in the chat.'),
        ],
    ]

    const applyBlocker = applyBlockers.find(([blocked]) => blocked)?.[1]

    const timedOut = isAxiosError(mutation.error) && mutation.error.response?.status === 504
    const serverError = isAxiosError(mutation.error) ? mutation.error.response?.data?.message : undefined
    const fallbackError = timedOut
        ? t('Generating the action proposal took too long. Try again. Your campaign settings were not changed.')
        : t('Could not configure actions automatically. Try again or configure them manually.')

    const restartBlocked = invalid || locallyInvalid || disconnected || !hasPrompt
    const chatDisabled = mutation.isPending || stale || disconnected || locallyInvalid
    const showQuestions = asking && !mutation.isPending && !stale
    const showInstructionsButton = !autoStart && !proposal && !mutation.isPending
    const instructionsBlocked = connections.isPending || disconnected || invalid || !hasPrompt
    const reviewDisabled = !proposal || mutation.isPending || stale

    return (
        <Modal
            open
            width={1140}
            title={t('Set up apps with Evaa')}
            onCancel={onClose}
            footer={
                <Flex
                    justify="end"
                    align="center"
                    gap="small"
                    wrap
                >
                    {proposal && applyBlocker && !mutation.isPending && (
                        <Typography.Text
                            type="warning"
                            className="mr-auto text-left"
                        >
                            {applyBlocker}
                        </Typography.Text>
                    )}
                    <Button onClick={onClose}>{t('Cancel')}</Button>
                    {proposal && (
                        <Button
                            loading={mutation.isPending}
                            disabled={restartBlocked}
                            onClick={() => void generate({ restart: true })}
                        >
                            {t('Start a new proposal')}
                        </Button>
                    )}
                    <Button
                        type="primary"
                        disabled={!!applyBlocker}
                        onClick={apply}
                    >
                        {draft.length ? t('Allow and use') : t('Use this configuration')}
                    </Button>
                </Flex>
            }
        >
            <Flex
                vertical
                gap="small"
                className="max-h-[72vh] overflow-y-auto"
            >
                <Typography.Text type="secondary">
                    {t(
                        'Evaa helps you set up these apps. Review what she can do before using the configuration. Nothing runs during setup.'
                    )}
                </Typography.Text>
                {!hasPrompt && (
                    <Alert
                        type="info"
                        message={t('Add campaign instructions first')}
                    />
                )}
                {stale && (
                    <Alert
                        type="warning"
                        message={t('Campaign settings changed. Generate a new proposal before applying.')}
                    />
                )}
                {mutation.isError && (
                    <Alert
                        type="error"
                        message={serverError ?? fallbackError}
                        action={
                            <Button
                                size="small"
                                onClick={() => void generate(lastAttempt.current)}
                            >
                                {t('Retry')}
                            </Button>
                        }
                    />
                )}
                {!connections.isPending && disconnected && (
                    <Alert
                        type="warning"
                        message={t('This app is disconnected. Connect it again and select the action.')}
                    />
                )}
                <Flex
                    gap="middle"
                    className="flex-col lg:h-[62vh] lg:flex-row"
                >
                    <Card
                        size="small"
                        title={t('Tell Evaa what you need')}
                        className="flex h-[60vh] min-h-0 min-w-0 flex-col lg:h-full lg:flex-1"
                        classNames={{ body: 'flex min-h-0 flex-1 flex-col' }}
                    >
                        <Chat
                            fill
                            messages={messages}
                            disabled={chatDisabled}
                            showTyping={mutation.isPending}
                            placeholder={chatPlaceholder(t, asking, !!proposal)}
                            onMessageAdded={(message) => void generate({ message })}
                            footer={
                                showQuestions && (
                                    <QuestionForm
                                        key={proposal?.proposalId ?? undefined}
                                        questions={questions}
                                        value={answers}
                                        disabled={disconnected || locallyInvalid}
                                        onChange={setAnswers}
                                        onSubmit={submitAnswers}
                                    />
                                )
                            }
                        />
                        {showInstructionsButton && (
                            <Button
                                className="mt-4"
                                disabled={instructionsBlocked}
                                onClick={() => void generate({})}
                            >
                                {t('Use my campaign instructions')}
                            </Button>
                        )}
                    </Card>
                    <Card
                        size="small"
                        title={t('What Evaa will be able to do')}
                        className="flex min-w-0 flex-col lg:h-full lg:flex-1"
                        classNames={{ body: 'min-h-0 flex-1 overflow-y-auto' }}
                    >
                        {!proposal && (
                            <Typography.Paragraph type="secondary">
                                {draft.length
                                    ? t('This is what I can do now. Tell me in the chat what you want to change.')
                                    : t('Your proposal will appear here. You can change it before allowing anything.')}
                            </Typography.Paragraph>
                        )}
                        {proposal && draft.length === 0 && (
                            <Typography.Text type="secondary">
                                {t('No app capabilities are needed for this proposal.')}
                            </Typography.Text>
                        )}
                        <fieldset
                            disabled={reviewDisabled}
                            className="m-0 min-w-0 border-0 p-0"
                        >
                            <Flex
                                vertical
                                gap="small"
                            >
                                <CapabilityReview
                                    key={proposal?.proposalId ?? undefined}
                                    tools={draft}
                                    connections={connections.data ?? []}
                                    baseline={proposal ? baseline : undefined}
                                    disabled={reviewDisabled}
                                    showApproval={false}
                                    invalidKeys={invalidDraft}
                                    onChange={changeDraft}
                                    onValidityChange={markInvalid}
                                />
                            </Flex>
                        </fieldset>
                    </Card>
                </Flex>
            </Flex>
        </Modal>
    )
}

// Watch a campaign field, also while the wizard step that holds it is not mounted.
function useCampaignField<T>(form: FormInstance, name: string | string[]): T | undefined {
    const watched = Form.useWatch(name, {
        form,
        preserve: true,
    }) as T | undefined

    return watched ?? form.getFieldValue(name)
}

// A proposal only applies to the campaign settings it was requested for.
function useRequestSnapshot(value: object) {
    const snapshot = JSON.stringify(value)
    const current = useRef(snapshot)
    const [requested, setRequested] = useState(snapshot)

    current.current = snapshot

    return {
        snapshot,
        stale: snapshot !== requested,
        isStillCurrent: (sent: string) => current.current === sent,
        markRequested: () => setRequested(snapshot),
    }
}

function hasDisconnectedApp(connected: AppConnection[], connectionIds: string[], draft: ToolSelection[]) {
    const isConnected = (connectionId: string) => connected.some((app) => app.id === connectionId)

    return !connectionIds.every(isConnected) || !draft.every((tool) => isConnected(tool.connectionId))
}

// A free chat message also sends the questions that are already answered.
function answersToSend(
    questions: ConfigurationQuestion[],
    given: Record<string, AnswerDraft>,
    sendAnswers: boolean,
    text: string | undefined
): ConfigurationAnswer[] {
    const answeredQuestions = sendAnswers
        ? questions
        : questions.filter((question) => answered(question, given[question.id]) && answersComplete([question], given))

    if (!answeredQuestions.length || (!sendAnswers && !text)) {
        return []
    }

    return serializeAnswers(answeredQuestions, given)
}

function proposalRequest({ input, text, restart, proposalId, draft, answers }: ProposalRequest): ConfigureToolsInput {
    const request: ConfigureToolsInput = { ...input }

    if (text) {
        request.message = text
    }

    // A new proposal starts without the earlier draft and answers.
    if (restart) {
        return request
    }

    if (proposalId) {
        request.proposalId = proposalId
        request.draftTools = toolConfiguration(draft)
    }

    if (answers.length) {
        request.answers = answers
    }

    return request
}

function chatPlaceholder(t: TFunction, asking: boolean, hasProposal: boolean) {
    if (asking) {
        return t('Or type something else…')
    }

    if (hasProposal) {
        return t('What would you like to change?')
    }

    return t('What should Evaa do?')
}
